import { useResizeObserver } from "@/src/lib/useResizeObserver";
import { cn } from "@/src/lib/utils";
import { useState } from "react";

export type StepsProps<Items extends readonly string[]> = {
  className?: string;
  step: Items[number];
  items: Items;
  onStepChange?: (step: Items[number]) => void;
  /** Optional display names; step values stay stable for routing/state. */
  labels?: Record<string, string>;
};

export function Steps<Items extends readonly string[]>({ items, step, className, onStepChange, labels }: StepsProps<Items>) {
  const currentIndex = items.indexOf(step);

  const [midpoints, setMidpoints] = useState([] as number[]);
  const itemsRef = useResizeObserver(([ev]) => {
    const containerX = ev.target.getBoundingClientRect().x;
    const midpoints = Array.from(ev.target.children).map((child) => midX(child.getBoundingClientRect()) - containerX);
    setMidpoints(midpoints);
  });

  return (
    <div className={cn("w-full flex flex-col gap-4 items-stretch", className)}>
      <div className="py-2.5 px-6 flex items-center justify-between" ref={itemsRef}>
        {items.map((item, index) => (
          <span
            onClick={onStepChange ? () => onStepChange(item) : undefined}
            key={item}
            className={cn("px-4 text-sm leading-6", {
              "text-blue-600": currentIndex === index,
              "text-gray-500": currentIndex < index,
              "cursor-pointer": onStepChange,
            })}
          >
            {labels?.[item] ?? item}
          </span>
        ))}
      </div>
      <div className="h-4 w-full border border-blue-100 bg-white rounded-full overflow-hidden">
        <div
          className="h-full rounded-full bg-blue-600"
          style={{
            width: midpoints[currentIndex] ?? 0,
          }}
        />
      </div>
    </div>
  );
}

const midX = (r: DOMRect) => r.x + r.width / 2;
